/**
 * Calendar difference in whole months plus leftover days, shared by the Date Calculator
 * and the Age Calculator.
 *
 * Convention for month-end dates: a month is complete when the same day number is reached
 * again; a day number the target month lacks (the 31st in a 30-day month, the 29th of
 * February in a common year) is counted as the first day of the following month, which is
 * what borrowing the length of the previous month does. So 31 August to 1 October is
 * 1 month 0 days, and 31 January to 1 March is 0 months 29 days (February never reaches
 * a 31st). The days are never negative, and the pair always adds back to the later date.
 */

const MS_PER_DAY = 86_400_000;

export interface MonthDiffDate {
  /** 1-12 */
  m: number;
  /** 1-31 */
  d: number;
  y: number;
}

function utcMs(y: number, monthIndex: number, d: number): number {
  const date = new Date(0);
  // setUTCFullYear keeps years 0-99 as written; Date.UTC would map them to 1900-1999.
  date.setUTCFullYear(y, monthIndex, d);
  return date.getTime();
}

/** UTC midnight of a date, in milliseconds. */
export function dateToUtcMs(date: MonthDiffDate): number {
  return utcMs(date.y, date.m - 1, date.d);
}

/**
 * Whole months and leftover days from `from` up to `to` (`from` on or before `to`).
 * Returns null when `to` is earlier than `from`.
 */
export function monthsAndDaysBetween(
  from: MonthDiffDate,
  to: MonthDiffDate
): { months: number; days: number } | null {
  const fromMs = dateToUtcMs(from);
  const toMs = dateToUtcMs(to);
  if (toMs < fromMs) {
    return null;
  }
  let months = (to.y - from.y) * 12 + (to.m - from.m);
  let anchorMs = utcMs(from.y, from.m - 1 + months, from.d);
  while (months > 0 && anchorMs > toMs) {
    months -= 1;
    anchorMs = utcMs(from.y, from.m - 1 + months, from.d);
  }
  return { months, days: Math.round((toMs - anchorMs) / MS_PER_DAY) };
}
